import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Calendar, Clock, CheckCircle, AlertTriangle, Banknote } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { adminService, bookingService } from '../../services/api';

interface DashboardStats {
  total_apartments: number;
  total_bookings: number;
  revenue_this_month: number;
  occupation_rate: number;
  recent_bookings: any[];
}

interface BookingCounts {
  pending: number;
  confirmed: number;
  pendingBookings: any[];
}

const Dashboard: React.FC = () => {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats>({
    total_apartments: 0,
    total_bookings: 0,
    revenue_this_month: 0,
    occupation_rate: 0,
    recent_bookings: []
  });
  const [bookingCounts, setBookingCounts] = useState<BookingCounts>({
    pending: 0,
    confirmed: 0,
    pendingBookings: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashboardRes, bookingsRes] = await Promise.all([
          adminService.getDashboard(),
          bookingService.getGerantBookings()
        ]);
        
        if (dashboardRes.data?.stats) {
          setStats({
            total_apartments: dashboardRes.data.stats.total_apartments || 0,
            total_bookings: dashboardRes.data.stats.total_bookings || 0,
            revenue_this_month: dashboardRes.data.stats.this_month_revenue || 0,
            occupation_rate: dashboardRes.data.occupancy_rate || 0,
            recent_bookings: dashboardRes.data.recent_bookings || []
          });
        }

        // Compter les réservations par statut
        const bookings = bookingsRes.data.bookings || [];
        const pending = bookings.filter((b: any) => b.status === 'pending');
        const confirmed = bookings.filter((b: any) => b.status === 'confirmed');
        setBookingCounts({
          pending: pending.length,
          confirmed: confirmed.length,
          pendingBookings: pending
        });
      } catch (error: any) {
        console.error('Erreur chargement dashboard:', error.response?.data || error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatDate = (date: string) => new Date(date).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'short'
  });

  const getClientName = (booking: any) => {
    if (booking.locataire) return `${booking.locataire.prenom} ${booking.locataire.nom}`;
    if (booking.guest) return `${booking.guest.name} (Guest)`;
    return 'Client';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-neutral-900">Tableau de bord</h1>
        <p className="text-neutral-600">Bienvenue, {user?.prenom} ! Gérez vos appartements et réservations.</p>
      </div>

      {/* Alerte réservations en attente */}
      {bookingCounts.pending > 0 && (
        <Link to="/gerant/bookings" className="block bg-orange-50 border-2 border-orange-300 rounded-xl p-4 hover:bg-orange-100 transition">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-orange-800 text-lg">
                {bookingCounts.pending} réservation{bookingCounts.pending > 1 ? 's' : ''} en attente de validation
              </p>
              <p className="text-orange-700 text-sm">Cliquez pour valider les paiements</p>
            </div>
            <Banknote className="w-8 h-8 text-orange-500" />
          </div>
        </Link>
      )}
      
      {/* Stats principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/gerant/apartments" className="card p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500">Appartements</p>
              <p className="text-2xl font-bold">{loading ? '...' : stats.total_apartments}</p>
            </div>
            <Building2 className="w-10 h-10 text-primary-500 opacity-80" />
          </div>
        </Link>
        
        <Link to="/gerant/bookings" className="card p-6 hover:shadow-lg transition-shadow border-2 border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">⏳ En attente</p>
              <p className="text-2xl font-bold text-orange-600">{loading ? '...' : bookingCounts.pending}</p>
            </div>
            <Clock className="w-10 h-10 text-orange-500 opacity-80" />
          </div>
        </Link>

        <div className="card p-6 border-2 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">✓ Confirmées</p>
              <p className="text-2xl font-bold text-green-600">{loading ? '...' : bookingCounts.confirmed}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-500 opacity-80" />
          </div>
        </div>
        
        <Link to="/gerant/bookings" className="card p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500">Total réservations</p>
              <p className="text-2xl font-bold">{loading ? '...' : stats.total_bookings}</p>
            </div>
            <Calendar className="w-10 h-10 text-primary-500 opacity-80" />
          </div>
        </Link>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Réservations en attente */}
        <div className="card p-6 border-2 border-orange-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-orange-800">🔔 À valider</h2>
            {bookingCounts.pending > 0 && (
              <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                {bookingCounts.pending}
              </span>
            )}
          </div>
          {bookingCounts.pendingBookings.length > 0 ? (
            <div className="space-y-3">
              {bookingCounts.pendingBookings.slice(0, 5).map((booking: any) => (
                <div key={booking.id} className="flex justify-between items-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <div>
                    <p className="font-medium">{booking.apartment?.titre || 'Appartement'}</p>
                    <p className="text-sm text-neutral-600">{getClientName(booking)}</p>
                    <p className="text-xs text-neutral-500">
                      {formatDate(booking.date_debut)} → {formatDate(booking.date_fin)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary-600">
                      {new Intl.NumberFormat('fr-FR').format(booking.montant_total)} F
                    </p>
                    <span className="text-xs text-orange-600">En attente</span>
                  </div>
                </div>
              ))}
              <Link to="/gerant/bookings" className="btn btn-primary w-full mt-2">
                Valider les paiements
              </Link>
            </div>
          ) : (
            <div className="text-center py-6">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-2" />
              <p className="text-green-600 font-medium">Aucune réservation en attente</p>
              <p className="text-neutral-500 text-sm">Toutes les réservations sont à jour</p>
            </div>
          )}
        </div>
        
        {/* Actions rapides */}
        <div className="card p-6">
          <h2 className="font-semibold mb-4">Actions rapides</h2>
          <div className="space-y-2">
            <Link to="/gerant/apartments/new" className="btn btn-primary w-full">
              Ajouter un appartement
            </Link>
            <Link to="/gerant/apartments" className="btn btn-outline w-full">
              Gérer mes appartements
            </Link>
            <Link to="/gerant/bookings" className="btn btn-outline w-full">
              Toutes les réservations
            </Link>
            <Link to="/gerant/calendar" className="btn btn-outline w-full">
              Voir le calendrier
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
